export type FlushCallback<T> = (elements: T[]) => void;

// AutoFlushingQueue queues elements and flushes those elements periodically or when the max queue size is reached.
// flushCallback is invoked whenever the queue is flushed with the list of all of the currently enqueued elements.
export class AutoFlushingQueue<T> {
  private _queue: T[] = [];
  private _flushTimeout?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly _flushIntervalMs: number,
    private readonly _maxQueueSize: number,
    private readonly _flushCallback: FlushCallback<T>
  ) {}

  push(element: T): void {
    this._queue.push(element);

    if (this._queue.length >= this._maxQueueSize) {
      this.flush();
    } else if (!this._flushTimeout) {
      this._scheduleFlush();
    }
  }

  flush(): void {
    if (this._queue.length === 0) {
      return;
    }

    clearTimeout(this._flushTimeout);
    this._flushTimeout = undefined;

    const elementsToFlush = this._queue;
    this._queue = [];

    this._flushCallback(elementsToFlush);
  }

  private _scheduleFlush(): void {
    this._flushTimeout = setTimeout(() => {
      this.flush();
    }, this._flushIntervalMs);
  }

  destroy(): void {
    clearTimeout(this._flushTimeout);
    this._flushTimeout = undefined;
  }
}
